import { useEffect, useState, useCallback } from 'react';
import dayAwayService, { TimeOffEntry } from '@/services/dayAwayService';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import InfiniteScroll from 'react-infinite-scroll-component';
import { format, parseISO } from 'date-fns';

const Ledger = () => {
  const [entries, setEntries] = useState<TimeOffEntry[]>([]);
  const [displayedEntries, setDisplayedEntries] = useState<TimeOffEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState<string>('all');
  const [selectedEmployee, setSelectedEmployee] = useState<string>('all');
  const [employees, setEmployees] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const ITEMS_PER_PAGE = 20;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // Fetch both ledger entries and employee names
        const [data, employeeNames] = await Promise.all([
          dayAwayService.getLedgerEntries(),
          dayAwayService.getEmployeeNames()
        ]);
        
        console.log('Fetched data:', data);
        console.log('Employee names:', employeeNames);
        
        // Sort entries by date (newest first)
        const sortedData = data.sort((a, b) =>
          new Date(b.date).getTime() - new Date(a.date).getTime()
        );
        setEntries(sortedData);

        // Use the same employee names as Dashboard
        setEmployees(employeeNames);

        // Initialize displayed entries
        setDisplayedEntries(sortedData.slice(0, ITEMS_PER_PAGE));
      } catch (error) {
        console.error('Error fetching ledger entries:', error);
        setError('Failed to load ledger entries. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const filterEntries = useCallback(() => {
    let filtered = [...entries];
    console.log('Initial entries:', filtered.length);

    // Apply search filter
    if (searchQuery) {
      filtered = filtered.filter(entry =>
        entry.note?.toLowerCase().includes(searchQuery.toLowerCase())
      );
      console.log('After search filter:', filtered.length);
    }

    // Apply type filter
    if (selectedType !== 'all') {
      filtered = filtered.filter(entry => entry.timeOffType === selectedType);
      console.log('After type filter:', filtered.length);
    }

    // Apply employee filter
    if (selectedEmployee !== 'all') {
      filtered = filtered.filter(entry => entry.employee === selectedEmployee);
      console.log('After employee filter:', filtered.length);
    }

    console.log('Final filtered entries:', filtered);
    return filtered;
  }, [entries, searchQuery, selectedType, selectedEmployee]);

  useEffect(() => {
    const filtered = filterEntries();
    setDisplayedEntries(filtered.slice(0, ITEMS_PER_PAGE));
    setPage(1);
  }, [filterEntries, ITEMS_PER_PAGE]);

  const loadMore = () => {
    const filtered = filterEntries();
    const nextItems = filtered.slice(0, (page + 1) * ITEMS_PER_PAGE);
    setDisplayedEntries(nextItems);
    setPage(page + 1);
  };

  if (isLoading && displayedEntries.length === 0) {
    return (
      <div className="p-8">
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-gray-200 dark:border-gray-700 border-t-blue-600"></div>
          <p className="mt-2 text-gray-600 dark:text-gray-400">Loading entries...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Time Off Ledger</h1>
        <p className="text-gray-500 dark:text-gray-400">View and filter all time off entries</p>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Search Notes</label>
              <Input
                placeholder="Search by note..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Employee</label>
              <Select
                value={selectedEmployee}
                onValueChange={setSelectedEmployee}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Employee" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Employees</SelectItem>
                  {employees
                    .filter(employee => employee && employee.trim() !== '')
                    .map((employee) => (
                      <SelectItem key={employee} value={employee}>
                        {employee}
                      </SelectItem>
                    )
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Type</label>
              <Select
                value={selectedType}
                onValueChange={setSelectedType}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="Full">Full Day</SelectItem>
                  <SelectItem value="Half">Half Day</SelectItem>
                  <SelectItem value="Email Only">Email Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-800 dark:text-red-300">
          {error}
        </div>
      )}

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="min-w-full">
            <div className="bg-gray-50 dark:bg-gray-800/50 border-b dark:border-gray-700">
              <div className="grid grid-cols-6 px-6 py-2.5 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                <div>Date</div>
                <div>Employee</div>
                <div>Type</div>
                <div className="col-span-2">Note</div>
                <div>Status</div>
              </div>
            </div>

            <div id="scrollableDiv" className="overflow-auto" style={{ maxHeight: 'calc(100vh - 400px)' }}>
              {isLoading && displayedEntries.length === 0 ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-gray-200 dark:border-gray-700 border-t-blue-600"></div>
                  <p className="mt-2 text-gray-600 dark:text-gray-400">Loading entries...</p>
                </div>
              ) : (
                <InfiniteScroll
                  dataLength={displayedEntries.length}
                  next={loadMore}
                  hasMore={displayedEntries.length < filterEntries().length}
                  loader={
                    <div className="text-center py-4">
                      <div className="inline-block animate-spin rounded-full h-6 w-6 border-4 border-gray-200 dark:border-gray-700 border-t-blue-600"></div>
                      <p className="mt-2 text-gray-600 dark:text-gray-400">Loading more entries...</p>
                    </div>
                  }
                  scrollableTarget="scrollableDiv"
                >
                  {displayedEntries.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      No entries found matching your filters.
                    </div>
                  ) : (
                    displayedEntries.map((entry, index) => (
                      <div
                        key={entry.id || `${entry.date}-${entry.employee}-${index}`}
                        className="grid grid-cols-6 px-6 py-3 border-b dark:border-gray-700 text-sm text-gray-900 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/50 items-center transition-colors duration-150 cursor-pointer"
                      >
                        <div className="text-gray-700 dark:text-gray-300">
                          {(() => {
                            try {
                              return format(parseISO(entry.date), 'MM/dd/yyyy');
                            } catch (error) {
                              // Fallback for non-ISO date formats
                              try {
                                return entry.date;
                              } catch (e) {
                                return 'Invalid date';
                              }
                            }
                          })()}
                        </div>
                        <div>
                          <span className="inline-flex items-center rounded-md bg-blue-50 dark:bg-blue-900/50 px-2 py-1 text-xs font-medium text-blue-700 dark:text-blue-300 ring-1 ring-inset ring-blue-700/10 dark:ring-blue-400/20 hover:bg-blue-100 dark:hover:bg-blue-900/70 transition-colors duration-150">
                            {entry.employee}
                          </span>
                        </div>
                        <div>
                          {entry.timeOffType === 'Full' && (
                            <span className="inline-flex items-center rounded-md bg-green-50 dark:bg-green-900/50 px-2 py-1 text-xs font-medium text-green-700 dark:text-green-300 ring-1 ring-inset ring-green-600/10 dark:ring-green-400/20">
                              Full Day
                            </span>
                          )}
                          {entry.timeOffType === 'Half' && (
                            <span className="inline-flex items-center rounded-md bg-yellow-50 dark:bg-yellow-900/50 px-2 py-1 text-xs font-medium text-yellow-700 dark:text-yellow-300 ring-1 ring-inset ring-yellow-600/10 dark:ring-yellow-400/20">
                              Half Day
                            </span>
                          )}
                          {entry.timeOffType === 'Email Only' && (
                            <span className="inline-flex items-center rounded-md bg-purple-50 dark:bg-purple-900/50 px-2 py-1 text-xs font-medium text-purple-700 dark:text-purple-300 ring-1 ring-inset ring-purple-600/10 dark:ring-purple-400/20">
                              Email Only
                            </span>
                          )}
                        </div>
                        <div className="col-span-2">
                          <span className="text-gray-600 dark:text-gray-400">{entry.note}</span>
                        </div>
                        <div>
                          {entry.status === 'Good-to-Go' && (
                            <span className="inline-flex items-center rounded-md bg-green-50 dark:bg-green-900/50 px-2 py-1 text-xs font-medium text-green-700 dark:text-green-300 ring-1 ring-inset ring-green-600/10 dark:ring-green-400/20">
                              Good-to-Go
                            </span>
                          )}
                          {entry.status === 'Old Entry' && (
                            <span className="inline-flex items-center rounded-md bg-gray-50 dark:bg-gray-800 px-2 py-1 text-xs font-medium text-gray-600 dark:text-gray-400 ring-1 ring-inset ring-gray-500/10 dark:ring-gray-500/20">
                              Old Entry
                            </span>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </InfiniteScroll>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Ledger;
